// From fraction
// http://stackoverflow.com/questions/3700326/decode-amp-back-to-in-javascript
export function decodeHTML(s: string) {
  const elem = document.createElement("textarea");
  elem.innerHTML = s;
  return elem.value;
}

export function getFileExtension(path: string): string {
  path = path.split("?")[0];
  const index = path.lastIndexOf(".");
  if (index > -1 && index < path.length - 2) {
    const result = path.substring(index + 1).toLowerCase();
    if (result.indexOf("/") === -1) {
      return result;
    }
  }
  return "";
}

// http://erlycoder.com/49/javascript-hash-functions-to-convert-string-into-integer-hash-
// Used in fraction, required for interoperability with stored email-based hashes
export function hash(s: string) {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + (hash << 6) + (hash << 16) - hash;
  }
  return hash;
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
export const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
};

export interface ExcerptTextOptions {
  /** guide for length of excerpt, default 100 */
  length?: number;
  /** string for ellipsis, default horizontal ellipsis char */
  ellipsis?: string;
  /** when trying to snap to word boundaries, how far to go each side, default 12 */
  tolerance?: number;
  /** an optional starting position */
  startPos?: number;
}

export function excerptText(
  s: string,
  term: string,
  options?: ExcerptTextOptions
) {
  const {
    length = 100,
    ellipsis = "…",
    tolerance = 12,
    startPos = 0,
  } = options ?? {};
  const pos = s.toLowerCase().indexOf(term.toLowerCase(), startPos);
  if (pos === -1) {
    return s.length <= length ? s : s.substring(0, length) + ellipsis;
  }
  if (s.length <= length) {
    return s;
  }
  const before = s.substring(0, pos);
  const after = s.substring(pos + term.length);
  // snap to word boundary utils
  const getBefore = (inset: number) => {
    const i = before.length - inset;
    if (before.charAt(i) !== " ") {
      const padLeft = before.substring(0, before.length - inset);
      const lastSpace = padLeft.lastIndexOf(" ");
      inset +=
        lastSpace === -1
          ? Math.min(padLeft.length, tolerance - 1)
          : padLeft.length - lastSpace - 1;
    }
    return (inset < pos ? ellipsis : "") + before.slice(-inset).trimStart();
  };
  const getAfter = (inset: number) => {
    if (after.charAt(inset) !== " ") {
      const lastSpace = after.indexOf(" ", inset);
      inset +=
        lastSpace === -1
          ? Math.min(after.length - inset, tolerance - 2)
          : lastSpace - inset - 1;
    }
    return (
      after.substring(0, inset + 1).trimEnd() +
      (inset < after.length ? ellipsis : "")
    );
  };

  const pad = Math.floor((length - term.length) / 2);
  if (before.length > pad && after.length > pad) {
    return `${getBefore(pad)}${term}${getAfter(pad)}`;
  } else if (before.length < pad) {
    return `${before}${term}${getAfter(pad)}`;
  } else {
    return `${getBefore(pad)}${term}${after}`;
  }
}

export const boldSubstring = (string: string, substring: string) => {
  return string?.replace(
    new RegExp(`(${escapeRegExp(substring)})`, "gi"),
    function (str) {
      return `<b>${str}</b>`;
    }
  );
};
